/**
 * ServiceCatalogFilters - filter controls for the service catalog
 */
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ServiceCatalogFilters as FilterOptions } from '@/lib/types'

// Sentinel value for "all" option since Radix Select doesn't allow empty string
const ALL_VALUE = '__all__'

interface ServiceCatalogFiltersProps {
  /** Current search query */
  search: string
  /** Called when search query changes */
  onSearchChange: (value: string) => void
  /** Currently selected area path filter */
  areaPath: string
  /** Called when area path filter changes */
  onAreaPathChange: (value: string) => void
  /** Currently selected owner team filter */
  ownerTeam: string
  /** Called when owner team filter changes */
  onOwnerTeamChange: (value: string) => void
  /** Currently selected country filter */
  country: string
  /** Called when country filter changes */
  onCountryChange: (value: string) => void
  /** Available filter options */
  availableFilters?: FilterOptions
}

/**
 * Filter controls for the service catalog.
 * Includes search input and dropdown filters for area path and owner team.
 */
export function ServiceCatalogFilters({
  search,
  onSearchChange,
  areaPath,
  onAreaPathChange,
  ownerTeam,
  onOwnerTeamChange,
  country,
  onCountryChange,
  availableFilters,
}: ServiceCatalogFiltersProps) {
  // Convert empty string to sentinel for Select component
  const areaPathValue = areaPath || ALL_VALUE
  const ownerTeamValue = ownerTeam || ALL_VALUE
  const countryValue = country || ALL_VALUE

  // Convert sentinel back to empty string when value changes
  const handleAreaPathChange = (value: string) => {
    onAreaPathChange(value === ALL_VALUE ? '' : value)
  }

  const handleOwnerTeamChange = (value: string) => {
    onOwnerTeamChange(value === ALL_VALUE ? '' : value)
  }

  const handleCountryChange = (value: string) => {
    onCountryChange(value === ALL_VALUE ? '' : value)
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Search Input */}
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search services..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Area Path Filter */}
      <Select value={areaPathValue} onValueChange={handleAreaPathChange}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="All Area Paths" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Area Paths</SelectItem>
          {availableFilters?.areaPaths.map((path) => (
            <SelectItem key={path} value={path}>
              {path.split('\\').pop() || path}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Owner Team Filter */}
      <Select value={ownerTeamValue} onValueChange={handleOwnerTeamChange}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="All Teams" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Teams</SelectItem>
          {availableFilters?.ownerTeams.map((team) => (
            <SelectItem key={team} value={team}>
              {team}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Country Filter */}
      <Select value={countryValue} onValueChange={handleCountryChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="All Countries" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Countries</SelectItem>
          <SelectItem value="Global">Global</SelectItem>
          {availableFilters?.countries?.map((c) => (
            <SelectItem key={c} value={c}>
              {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
